import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAppContext } from '../context/AppContext';
import { Card } from '../components/Card';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
import { User, CreditCard, Activity } from 'lucide-react';
import { Box, Typography, Stack, Grid, LinearProgress, Alert, Divider } from '@mui/material';

export default function Settings() {
  const { user, usage } = useAppContext();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [isSaving, setIsSaving] = useState(false);

  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage(null);

    try {
      const { error } = await supabase.auth.updateUser({
        data: { full_name: name }
      });
      if (error) throw error;
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to save settings' });
    } finally {
      setIsSaving(true);
      setTimeout(() => setIsSaving(false), 500);
    }
  };

  const usagePercentage = Math.min(100, Math.round((usage.generationsUsed / usage.planLimit) * 100));

  return (
    <Box sx={{ maxWidth: 'md', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Box>
        <Typography variant="h5" fontWeight="bold" gutterBottom>Settings</Typography>
        <Typography variant="body2" color="text.secondary">Manage your account settings and preferences.</Typography>
      </Box>

      {/* Profile Settings */}
      <Card sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <User size={20} color="var(--mui-palette-primary-main)" />
          <Typography variant="h6" fontWeight="semibold">Profile Information</Typography>
        </Stack>
        <Divider sx={{ mb: 3 }} />

        {message && (
          <Alert severity={message.type} sx={{ mb: 3, borderRadius: 2 }}>
            {message.text}
          </Alert>
        )}

        <form onSubmit={handleSave}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Input
                label="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Input
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Grid>
          </Grid>
          <Box pt={1}>
            <Button type="submit" isLoading={isSaving} variant="primary">Save Changes</Button>
          </Box>
        </form>
      </Card>

      {/* Usage Meter */}
      <Card sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Activity size={20} color="#22c55e" />
          <Typography variant="h6" fontWeight="semibold">Usage Meter</Typography>
        </Stack>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" fontWeight="medium">Posts Generated</Typography>
          <Typography variant="body2" fontWeight="bold">{usage.generationsUsed} / {usage.planLimit}</Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={usagePercentage}
          color={usagePercentage >= 90 ? 'error' : 'primary'}
          sx={{ height: 10, borderRadius: 5, mb: 3 }}
        />

        {usagePercentage >= 80 && (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            You are approaching your plan limit. Upgrade to Pro for unlimited generations.
          </Alert>
        )}
      </Card>

      {/* Plan Details */}
      <Card sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <CreditCard size={20} color="#a855f7" />
          <Typography variant="h6" fontWeight="semibold">Plan Details</Typography>
        </Stack>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">{user?.plan} Plan</Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.plan === 'Free' ? '10 posts per month' : 'Unlimited posts'}
            </Typography>
          </Box>
          {user?.plan === 'Free' && (
            <Button variant="primary">Upgrade to Pro</Button>
          )}
        </Box>
      </Card>
    </Box>
  );
}
